import { useState } from 'react';
import { logoUrl } from '../api/client';
import { TRACKER_IDS, TRACKER_NAMES, type CasinoRow, type Payload, type Reading as TrackerReading, type TrackerId, type WindowKey } from '../api/types';
import { count, gapStep, median, pct, usd } from '../lib/format';
import { finite } from '../ui/charts';
import { CardHead, GlassColumns, GlassDonut, GlassHBars, Kpi, SmoothLines } from '../ui/glass';
import { Icon } from '../ui/Icon';
import { Logo } from '../ui/Logo';
import { Count } from '../ui/motion';

const ORDER: WindowKey[] = ['1d', '7d', '14d', '30d', '90d', '365d'];
const DAYS: Record<WindowKey, number> = { '1d': 1, '7d': 7, '14d': 14, '30d': 30, '90d': 90, '365d': 365 };
const WLABEL: Record<WindowKey, string> = { '1d': '24 hours', '7d': '7 days', '14d': '14 days', '30d': '30 days', '90d': '90 days', '365d': '1 year' };
const WSHORT: Record<WindowKey, string> = { '1d': '24h', '7d': '7d', '14d': '14d', '30d': '30d', '90d': '90d', '365d': '1y' };

type Pt = { day: string; usd: number };
type Bucket = { bucket: string; usd?: number | null; wallets?: number | null; pct?: number | null };
type Balance = { usd: number; wallets?: number | null };
type Chain = { chain: string; volume?: number | null };

// Each tracker publishes a different subset of extras, so these read them
// defensively: an absent field reads as absent, never as zero.
const dailyOf = (row: CasinoRow, id: TrackerId): Pt[] => {
  const raw = row.extra?.[id]?.daily;
  return Array.isArray(raw) ? raw.filter((p): p is Pt => !!p && typeof p.day === 'string' && finite(p.usd)) : [];
};
const distOf = (row: CasinoRow, id: TrackerId): Bucket[] => {
  const raw = row.extra?.[id]?.distribution;
  return Array.isArray(raw) ? raw.filter((b): b is Bucket => !!b && typeof b.bucket === 'string') : [];
};
const balOf = (row: CasinoRow, id: TrackerId): Balance | null => {
  const b = row.extra?.[id]?.balances as Balance | null | undefined;
  return b && finite(b.usd) ? b : null;
};
const chainsOf = (row: CasinoRow, id: TrackerId): Chain[] =>
  (row.extra?.[id]?.chains ?? []).flatMap((c) => (c && finite(c.volume) ? [{ chain: c.chain, volume: c.volume }] : []));

/** A deposit-size bucket's lower bound, read from its label ("<$100", "$1K-5K", "$1M+"). */
function lowerBound(label: string): number {
  if (label.trim().startsWith('<')) return 0;
  const m = label.match(/\$?\s*([\d.]+)\s*([KkMm]?)/);
  if (!m) return 0;
  const n = Number(m[1]);
  return m[2]?.toLowerCase() === 'm' ? n * 1e6 : m[2]?.toLowerCase() === 'k' ? n * 1e3 : n;
}
// Four size tiers on the validated blue ordinal ramp (dataviz palette steps 250 → 600).
const TIERS = [
  { key: 't0', label: 'Under $1K', lo: 0, color: '#86b6ef' },
  { key: 't1', label: '$1K to $10K', lo: 1e3, color: '#5598e7' },
  { key: 't2', label: '$10K to $100K', lo: 1e4, color: '#2a78d6' },
  { key: 't3', label: '$100K and up', lo: 1e5, color: '#184f95' },
];

const FIELDS: { key: keyof TrackerReading; label: string; fmt: (v: number) => string }[] = [
  { key: 'value', label: 'Deposits', fmt: usd },
  { key: 'withdrawals', label: 'Withdrawals', fmt: usd },
  { key: 'netFlow', label: 'Net flow', fmt: usd },
  { key: 'count', label: 'Deposit count', fmt: count },
  { key: 'depositors', label: 'Depositors', fmt: count },
  { key: 'avgDeposit', label: 'Average deposit', fmt: usd },
  { key: 'newDeposits', label: 'New depositors', fmt: count },
  { key: 'share', label: 'Market share', fmt: (v) => pct(v) },
  { key: 'rank', label: 'Rank', fmt: (v) => `#${v}` },
];


export function CasinoProfile({ data, win, casinoKey }: { data: Payload; win: WindowKey; casinoKey: string }) {
  const a = data.analysis.volume[win];
  const row = a?.rows.find((r) => r.key === casinoKey)
    ?? ORDER.map((w) => data.analysis.volume[w]?.rows.find((r) => r.key === casinoKey)).find(Boolean);
  const [chainSrc, setChainSrc] = useState<TrackerId | null>(null);
  const [distSrc, setDistSrc] = useState<TrackerId | null>(null);

  if (!row) {
    return <div className="tile"><p className="empty">No tracker lists a casino called “{casinoKey}”. <a href={`/casinos?w=${win}`}>Back to casinos</a></p></div>;
  }

  const readings = TRACKER_IDS.flatMap((id) => {
    const v = row.by[id]?.value;
    return finite(v) ? [{ id, value: v, counted: row.consensusBy.includes(id) }] : [];
  });
  const values = readings.filter((r) => r.counted).map((r) => r.value);
  const lo = values.length ? Math.min(...values) : null;
  const hi = values.length ? Math.max(...values) : null;
  const meta = TRACKER_IDS.map((id) => row.extra?.[id]?.meta).find((m) => m && (m.license || m.foundedYear || m.url));
  const sentiment = data.analysis.sentiment?.rows.find((s) => s.key === row.key);
  const logo = row.logos && Object.keys(row.logos).length ? logoUrl(row.key) : null;
  const step = gapStep(row.spreadPct);

  // KPIs
  const withdrawals = median(TRACKER_IDS.map((id) => row.by[id]?.withdrawals));
  const netFlow = median(TRACKER_IDS.map((id) => row.by[id]?.netFlow));
  const balances = TRACKER_IDS.flatMap((id) => { const b = balOf(row, id); return b ? [{ id, ...b }] : []; });
  const balMedian = median(balances.map((b) => b.usd));
  const maxWallets = Math.max(0, ...balances.map((b) => (finite(b.wallets) ? b.wallets : 0)));
  const byWindow = ORDER.flatMap((w) => {
    const r = data.analysis.volume[w]?.rows.find((x) => x.key === row.key);
    return r ? [{ w, r }] : [];
  });
  const spreads = byWindow.filter(({ r }) => r.consensusBy.length > 1 && finite(r.spreadPct)).map(({ w, r }) => ({ w, v: r.spreadPct as number }));
  const primaryDaily = dailyOf(row, 'fairgambling').length ? dailyOf(row, 'fairgambling') : dailyOf(row, 'tanzanite');

  // Daily series per tracker, cut to the selected window. extras.daily is
  // window-independent (up to 120d), so without this a 7d page charts June.
  const span = DAYS[win] ?? 30;
  const lineSeries = TRACKER_IDS.map((id) => ({
    key: id, name: TRACKER_NAMES[id], color: `var(--tracker-${id})`,
    points: dailyOf(row, id).slice(-span).map((p) => ({ day: p.day.slice(0, 10), value: p.usd })),
  })).filter((s) => s.points.length >= 2);

  // Deposits against withdrawals
  const flowTrackers = TRACKER_IDS.filter((id) => finite(row.by[id]?.value) || finite(row.by[id]?.withdrawals));

  // Deposit sizes, folded into four tiers
  const distTrackers = TRACKER_IDS.filter((id) => distOf(row, id).length);
  const distId = distSrc && distTrackers.includes(distSrc) ? distSrc : distTrackers[0];
  const tiers = distId ? TIERS.map((t, i) => {
    const next = TIERS[i + 1]?.lo ?? Infinity;
    const value = distOf(row, distId).filter((b) => { const l = lowerBound(b.bucket); return l >= t.lo && l < next; }).reduce((s, b) => s + (b.usd ?? 0), 0);
    return { key: t.key, label: t.label, value, color: t.color, sub: usd(value) };
  }).filter((t) => t.value > 0) : [];
  const bigShare = tiers.length ? (tiers.filter((t) => t.key === 't3').reduce((s, t) => s + t.value, 0) / tiers.reduce((s, t) => s + t.value, 0)) * 100 : null;

  // Chains
  const chainTrackers = TRACKER_IDS.filter((id) => chainsOf(row, id).length);
  const chainId = chainSrc && chainTrackers.includes(chainSrc) ? chainSrc : chainTrackers[0];
  const chains = chainId ? chainsOf(row, chainId).sort((x, y) => (y.volume ?? 0) - (x.volume ?? 0)) : [];
  // Shares are of every chain the tracker lists; the card shows the top five.
  const chainTotal = chains.reduce((s, c) => s + (c.volume ?? 0), 0);

  // Ratings
  const ratings = sentiment ? ([
    ['circus.fyi score', sentiment.circusScore, 100],
    ['Trustpilot', sentiment.trustpilot, 5],
    ['Casino Guru', sentiment.casinoGuru, 10],
    ['WagerX', sentiment.wagerx, 10],
    ['CasinoRankr', sentiment.casinoRankr, 5],
  ] as const).filter(([, v]) => finite(v)) : [];

  // Daily average by window: each window divided by its days, so they compare
  const perDayTrackers = TRACKER_IDS.filter((id) => byWindow.some(({ r }) => finite(r.by[id]?.value)));

  return (
    <div className="page profile">
      {/* ---------------------------------------------------------- header */}
      <section className={`tile phead phead-${step}`}>
        <span className="phead-art" aria-hidden />
        <a className="back" href={`/casinos?w=${win}`}>← All casinos</a>
        <div className="phead-main">
          <Logo src={logo} name={row.name} className="phead-logo" />
          <div>
            <span className="phead-kicker">Casino profile</span>
            <h1>{row.name}</h1>
            <div className="tints">
              {meta?.license && <span className="tint tint-blue">{meta.license}</span>}
              {meta?.siteType && <span className="tint tint-violet">{meta.siteType}</span>}
              {finite(meta?.foundedYear) && <span className="tint tint-teal">Since {meta.foundedYear}</span>}
              {finite(row.spreadPct) && <span className={`tint tint-amber`}>{pct(row.spreadPct, 0)} apart</span>}
            </div>
          </div>
        </div>
        <div className="phead-info">
          <p className="phead-desc">
            Listed by <b>{row.reportedBy.length} of {TRACKER_IDS.length}</b> trackers, with {row.consensusBy.length} counted toward its median.
            {finite(lo) && finite(hi) && lo !== hi ? <> Over the last {WLABEL[win]} they put its deposits between <b>{usd(lo)}</b> and <b>{usd(hi)}</b>.</> : null}
          </p>
          <ul className="phead-meta">
            <li><Icon name="shield" size={16} />Licence <b>{meta?.license ?? '—'}</b></li>
            <li><Icon name="calendar" size={16} />Founded <b>{meta?.foundedYear ?? '—'}</b></li>
            <li><Icon name="globe" size={16} />{meta?.url ? <a href={meta.url} target="_blank" rel="noreferrer">Website ↗</a> : <span>No website listed</span>}</li>
          </ul>
          {/* The page's one primary action is the site's point: put this casino
              next to others. Reading the full table is the secondary one. */}
          <div className="phead-actions">
            <a className="btn btn-primary btn-md" href={`/matchup?c=${row.key}&w=${win}`}>Compare with others</a>
            <a className="btn btn-secondary btn-md" href="#readings">Every reading</a>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------ KPIs */}
      <div className="kpi-row">
        <section className="tile"><Kpi icon="money" tone="blue" label={`Median deposits · ${WSHORT[win]}`} value={<Count value={row.consensus} format={usd} />}
          sub={`${row.consensusBy.length} trackers counted`} spark={primaryDaily.slice(-20).map((p) => p.usd)} sparkColor="var(--blue)" /></section>
        <section className="tile"><Kpi icon="gap" tone="amber" label="How far apart" value={<Count value={row.spreadPct} format={(v) => pct(v, 0)} />}
          sub={spreads.length > 1 ? `in ${spreads.length} windows` : 'highest to lowest'} spark={spreads.map((s) => s.v)} sparkColor="var(--gap-2)" /></section>
        <section className="tile"><Kpi icon="flow" tone="violet" label="Median withdrawals" value={<Count value={withdrawals} format={usd} />}
          sub={finite(netFlow) ? `net flow ${usd(netFlow)}` : 'net flow not published'} /></section>
        <section className="tile"><Kpi icon="wallet" tone="teal" label="Hot wallets now" value={<Count value={balMedian} format={usd} />}
          sub={maxWallets ? `up to ${maxWallets} wallets watched` : `${balances.length} trackers report`} /></section>
      </div>

      {/* The cards sit on one 12-column grid, paired so each row's cards hold
          about the same amount: a wide chart beside a short list, three
          mid-sized cards, then the full-width comparisons. */}
      <div className="pgrid">
        <section className="tile span-8">
          <CardHead icon="line" tone="violet" title="Daily deposits" note={`Daily deposits, last ${WLABEL[win]}`} />
          <SmoothLines series={lineSeries} format={usd} height={200} />
        </section>
        <section className="tile span-4">
          <CardHead icon="rank" tone="blue" title="What each tracker says" note={`Deposits, last ${WLABEL[win]}`} />
          <GlassHBars items={readings.sort((x, y) => y.value - x.value).map((r) => ({
            key: r.id, value: r.value, display: usd(r.value), color: `var(--tracker-${r.id})`,
            sub: !r.counted ? 'reference only' : finite(row.consensus) && row.consensus ? `${r.value >= row.consensus ? '+' : ''}${pct(((r.value - row.consensus) / row.consensus) * 100, 0)} vs median` : '',
            label: TRACKER_NAMES[r.id],
          }))} />
          <p className="tile-foot">Median {usd(row.consensus)} · {finite(row.spreadPct) ? `${pct(row.spreadPct, 0)} from highest to lowest` : 'only one counted tracker'}</p>
        </section>

        <section className="tile span-4">
          <CardHead icon="flow" tone="violet" title="Money in and out" note={`Deposits against withdrawals, ${WLABEL[win]}`} />
          <GlassColumns
            groups={flowTrackers.map((id) => TRACKER_NAMES[id])}
            series={[
              { key: 'in', name: 'Deposits', color: 'var(--flow-in)', values: flowTrackers.map((id) => row.by[id]?.value ?? null) },
              { key: 'out', name: 'Withdrawals', color: 'var(--flow-out)', values: flowTrackers.map((id) => row.by[id]?.withdrawals ?? null) },
            ]}
            format={usd}
            height={150}
          />
          <p className="tile-foot">A dashed column is not published: missing is not zero.</p>
        </section>
        <section className="tile span-5">
          <CardHead icon="size" tone="blue" title="Deposit sizes" note="Share of volume by deposit size">
            {distTrackers.length > 1 && (
              <div className="seg seg-sm" role="group" aria-label="Tracker">
                {distTrackers.map((id) => <button key={id} aria-pressed={id === distId} onClick={() => setDistSrc(id)}>{TRACKER_NAMES[id]}</button>)}
              </div>
            )}
          </CardHead>
          <GlassDonut slices={tiers} centre={finite(bigShare) ? pct(bigShare, 0) : '—'} centreLabel="from $100K+" />
        </section>
        <section className="tile span-3">
          <CardHead icon="window" tone="amber" title="Does the gap hold?" note="Apart, window by window" />
          <GlassHBars items={spreads.map(({ w, v }) => ({ key: w, label: WLABEL[w], value: v, display: pct(v, 0), className: `g${gapStep(v)}` }))} />
          <p className="tile-foot">A gap in every window is a disagreement about the casino, not a noisy week.</p>
        </section>

        <section className="tile span-4">
          <CardHead icon="chain" tone="teal" title="Deposits by chain" note={chainId ? `As ${TRACKER_NAMES[chainId]} splits them` : 'Not published'}>
            {chainTrackers.length > 1 && (
              <div className="seg seg-sm" role="group" aria-label="Tracker">
                {chainTrackers.map((id) => <button key={id} aria-pressed={id === chainId} onClick={() => setChainSrc(id)}>{TRACKER_NAMES[id]}</button>)}
              </div>
            )}
          </CardHead>
          <GlassHBars items={chains.slice(0, 5).map((c) => ({
            key: c.chain, label: c.chain, value: c.volume ?? 0, display: usd(c.volume), color: chainId ? `var(--tracker-${chainId})` : 'var(--blue)',
            sub: chainTotal ? pct(((c.volume ?? 0) / chainTotal) * 100, 0) : '',
          }))} />
        </section>
        <section className="tile span-4">
          <CardHead icon="star" tone="magenta" title="Reputation" note="As the review sites publish it" />
          <GlassHBars max={100} items={ratings.map(([name, v, of]) => ({
            key: name, label: name, value: ((v as number) / of) * 100, display: `${v}/${of}`, color: 'var(--series-7)',
          }))} />
        </section>
        <section className="tile span-4">
          <CardHead icon="wallet" tone="teal" title="Hot-wallet balances" note="Right now, by tracker" />
          <GlassHBars items={balances.map((b) => ({
            key: b.id, label: TRACKER_NAMES[b.id], value: b.usd, display: usd(b.usd), color: `var(--tracker-${b.id})`,
            sub: finite(b.wallets) ? `${b.wallets} wallets` : 'wallets not published',
          }))} />
          <p className="tile-foot">Each tracker watches its own wallets, so a lower balance often means fewer wallets found, not less money.</p>
        </section>

        <section className="tile span-12">
          <CardHead icon="money" tone="blue" title="Daily average, by window" note="Each window’s deposits divided by its days, so windows of different lengths compare" />
          <GlassColumns
            groups={byWindow.map(({ w }) => WSHORT[w])}
            series={perDayTrackers.map((id) => ({
              key: id, name: TRACKER_NAMES[id], color: `var(--tracker-${id})`,
              values: byWindow.map(({ w, r }) => { const v = r.by[id]?.value; return finite(v) ? v / DAYS[w] : null; }),
            }))}
            format={usd}
            height={150}
          />
        </section>
      </div>

      {/* ------------------------------------------------------------ table */}
      <section className="tile" id="readings">
        <CardHead icon="table" tone="violet" title="Every reading" note={`${WLABEL[win]} · * published, not counted`} />
        <div className="t-wrap">
          <table className="t">
            <thead>
              <tr><th>Reading</th>{TRACKER_IDS.map((id) => <th key={id} className="r"><i className="gdot" style={{ background: `var(--tracker-${id})`, display: 'inline-block', marginRight: 8, width: 8, height: 8 }} aria-hidden />{TRACKER_NAMES[id]}</th>)}</tr>
            </thead>
            <tbody>
              {FIELDS.map((f) => {
                if (!TRACKER_IDS.some((id) => finite(row.by[id]?.[f.key]))) return null;
                return (
                  <tr key={f.key}>
                    <td>{f.label}</td>
                    {TRACKER_IDS.map((id) => {
                      const v = row.by[id]?.[f.key];
                      return <td key={id} className="r">{finite(v) ? f.fmt(v) : <span className="muted">—</span>}{finite(v) && f.key === 'value' && !row.consensusBy.includes(id) ? ' *' : ''}</td>;
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
